import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daihd2joh653ivfvoddg';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，新的事件梳理会第一时间推送给你。');
      form.reset();
      return;
    }

    formData.delete('contact_alt');
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = String(parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? '');
      const isSpam = serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('订阅成功，新的事件梳理会第一时间推送给你。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(isSpam ? '提交未通过校验，请检查后重试。' : serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <section id="subscribe" className="border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
        <div className="relative overflow-hidden rounded-lg border border-background-300 bg-accent-50">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 md:items-center md:gap-10 md:p-10">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                <i className="ri-flashlight-line text-sm" />
                事件提醒
              </span>
              <h2 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
                有新进展时，第一时间告诉你
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                只推送你关注的分区，事件出现关键节点或当事方回应时发送提醒，不做无关打扰。
              </p>
              <ul className="mt-4 space-y-2 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-base text-secondary-600" />
                  时间线更新即刻通知
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-base text-secondary-600" />
                  每周一次事件复盘摘要
                </li>
              </ul>
            </div>

            <form data-readdy-form id="subscribe-form" onSubmit={handleSubmit} className="rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
              <label htmlFor="sub-email" className="block text-sm font-semibold text-foreground-800">
                邮箱地址
              </label>
              <input
                id="sub-email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />

              <label htmlFor="sub-topic" className="mt-4 block text-sm font-semibold text-foreground-800">
                关注分区
              </label>
              <select
                id="sub-topic"
                name="topic"
                className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              >
                <option value="全部事件">全部事件</option>
                <option value="娱乐动态">娱乐动态</option>
                <option value="网红主播">网红主播</option>
                <option value="品牌商业">品牌商业</option>
                <option value="文娱演出">文娱演出</option>
              </select>

              <input
                className="alt-field"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <i className={status === 'loading' ? 'ri-loader-4-line animate-spin text-base' : 'ri-notification-3-line text-base'} />
                {status === 'loading' ? '提交中…' : '免费订阅更新'}
              </button>

              {message ? (
                <p
                  role="status"
                  className={`mt-3 rounded-md px-3 py-2 text-sm ${
                    status === 'success'
                      ? 'bg-secondary-100 text-secondary-800'
                      : 'bg-primary-100 text-primary-800'
                  }`}
                >
                  {message}
                </p>
              ) : (
                <p className="mt-3 text-xs text-foreground-500">我们不会向第三方分享你的邮箱，可随时退订。</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}